import React, { FC, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { twMerge } from "tailwind-merge";
import { LAMPORTS_PER_SOL } from "@solana/web3.js";

import { ModalProps } from "./types";
import { BaseModal, Button } from "../common";
import { SCROLLBAR_CLASSES_BLACK } from "../../styles/commonClasses";
import FormItem from "../common/form-item/FormItem";
import { NumberType, formatZeebitNumber } from "../../utils/currency/formatting";
import {
  NetworkContext, PlayerTokenContext, ToasterContext, BalanceContext, AggregatedBalancesContext
} from "../../contexts";
import { SessionAuthorityContext } from "../../contexts/SessionAuthorityContext";
import { BASE_TOAST_CONFIG, BaseToast } from "../toast/BaseToast";
import { getS3StaticFolderUrl } from "../../utils/config/utils";
import { TokenAmountField } from "../games/formGenerator/TokenAmoutInput";
import { LAMPORT_TOPUP_AUTO_SIGNER } from "../../sdk/constants";

export enum AutoSignTopUpType {
  LOW = 'low',
  TOP_UP = 'top up'
}

interface IAddFundsAndPlayModalProps extends ModalProps {
  onFundSuccess?: Function
  type?: AutoSignTopUpType
};

export const TransferToAutoSignBalanceModal: FC<IAddFundsAndPlayModalProps> = ({
  visible,
  hideModal,
  onFundSuccess,
  type = AutoSignTopUpType.TOP_UP
}) => {
  const { mergedTokens } = useContext(AggregatedBalancesContext);
  const { selectedTokenMeta } = useContext(BalanceContext);
  const selectedMerged = useMemo(() => {
    return mergedTokens?.find((token) => {
      return token.context?.symbol === "SOL"
    })
  }, [mergedTokens, selectedTokenMeta])

  const walletBalance = useMemo(() => {
    if (selectedMerged?.wallet == null) {
      return 0
    }
    return selectedMerged?.wallet.uiAmount;
  }, [selectedMerged]);

  const toast = useContext(ToasterContext);

  const [tokensAmountValue, setTokensAmountValue] = useState<number | string>();
  const { signerKp, allowsAutoSigning, topUpSol, lamportBalance } = useContext(SessionAuthorityContext);
  const { loadPlayerToken } = useContext(PlayerTokenContext);
  const [isPreview, setIsPreview] = useState(true)

  useEffect(() => {
    if (tokensAmountValue != null) {
      setIsPreview(false)
    }
  }, [tokensAmountValue])

  useEffect(() => {
    if (tokensAmountValue != null) {
      return
    }

    console.log(`INOT SETTING INIT VALUE`, {
      lamportBalance,
      type
    })

    // IF ITS LOW, NUDGE WITH TOPUP AMOUNT
    if ((lamportBalance || 0) < LAMPORT_TOPUP_AUTO_SIGNER) {
      const topUpAmountLamports = LAMPORT_TOPUP_AUTO_SIGNER - (lamportBalance || 0)
      const topUpAmountSol = topUpAmountLamports / LAMPORTS_PER_SOL
      const roundingFactor = 10_000
      const topUpAmountSolRounded = Math.round(topUpAmountSol * roundingFactor) / roundingFactor

      setTokensAmountValue(
        String(
          Math.max(
            topUpAmountSolRounded,
            0.008
          )
        ))
    } else {
      setTokensAmountValue(String(0))
    }
  }, [lamportBalance, type, tokensAmountValue])

  const lamportBalanceWithPrecision = Number(lamportBalance || 0) / LAMPORTS_PER_SOL;

  const [actionLoading, setActionLoading] = useState(false);
  const { client, recentBlockhash } = useContext(NetworkContext);

  const handleTopUpSolClick = useCallback(async () => {
    try {
      // IF INITIALISING THE ACCOUNT, MUST BE > 0.008 SOL
      if ((lamportBalance || 0) == 0) {
        if (tokensAmountValue <= 0.008) {
          toast(
            <BaseToast
              message={`You must deposit more than 0.008 Sol.`}
              type={"error"}
            />,
            BASE_TOAST_CONFIG,
          );

          return
        }
      }

      setActionLoading(true)

      // CONFIRM THE TX
      await topUpSol(Number(tokensAmountValue));

      toast(
        <BaseToast
          message={`Successfully deposited ${tokensAmountValue} ${selectedMerged?.context?.name}.`}
          type={"success"}
        />,
        BASE_TOAST_CONFIG,
      );

      onFundSuccess?.();
      // HIDE MODAL
      hideModal();
    } catch (err) {
      console.error({ err });
      toast(
        <BaseToast
          message={`There was an issue depositing tokens.`}
          type={"error"}
        />,
        BASE_TOAST_CONFIG,
      );
    } finally {
      setActionLoading(false);
    }
  }, [
    tokensAmountValue,
    selectedMerged,
    client,
    recentBlockhash,
    loadPlayerToken,
    signerKp,
    allowsAutoSigning
  ]);

  const amountError = Number(walletBalance) < Number(tokensAmountValue) ? "Insufficient funds" : "";
  const solGasFee = "0.00005";

  return (
    <BaseModal
      open={visible}
      withHeading={false}
      onClose={hideModal}
      classes={{
        dialog: `w-[342px] sm:max-w-[420px] bg-gray-800 p-5`
      }}
    >
      <div
        data-id="transfer-to-auto-balance"
        className="flex flex-col gap-3 sm:gap-5 items-center max-h-[70vh] relative"
      >
        <div
          className={twMerge(
            "flex flex-col overflow-y-auto w-full pr-3 -mr-3 gap-y-6",
            SCROLLBAR_CLASSES_BLACK
          )}
        >
          <div className="flex w-full flex-col gap-y-4 items-center">
            <div className="flex w-full mt-3 justify-center">
              <img src={getS3StaticFolderUrl("/static/auto-signing-icon.png")} className="flex w-1/2" />
            </div>

            <div
              className="flex flex-col items-center gap-y-2.5 self-stretch text-center"
            >
              <div className="text-xl font-semibold">Top Up Auto-Signing Keys</div>
              <div className="subtitle text-base font-normal text-gray-200">
                {type == AutoSignTopUpType.TOP_UP ? 'Add more SOL to Auto-Signing Keys to keep playing without signing manually.' : 'Your Auto-Signing Keys have run low on SOL. Please add more SOL to play without manual signing.'}
              </div>
            </div>

            <FormItem
              className="self-stretch [&>label]:font-normal"
              label="Amount"
              rightLabel={
                <div className="flex font-semibold">
                  In Wallet:
                  &nbsp;{
                    formatZeebitNumber(walletBalance, NumberType.TOKEN_AMOUNT)
                  }
                  &nbsp;{selectedMerged?.context?.symbol}
                </div>
              }
              error={amountError}
            >
              <TokenAmountField
                classes={{
                  label: amountError ? "" : "border-none",
                  input: "py-3",
                }}
                minWager={0}
                wager={tokensAmountValue}
                setWager={setTokensAmountValue}
                iconUrl={selectedMerged?.context?.imageDarkPng || ""}
                step={0.1}
                isLoading={actionLoading}
                maxWager={walletBalance}
                actions={["half", "max"]}
                error={amountError}
                isPreview={isPreview}
              />
            </FormItem>
          </div>

          <div className="flex w-full flex-col gap-y-3">
            <div className="flex-col text-gray-200 text-sm gap-1 bg-gray-600 rounded-md p-2 font-normal">
              <div className="flex justify-between">
                <div>In Signing Wallet:</div>
                <div>{lamportBalanceWithPrecision} {selectedMerged?.context?.symbol}</div>
              </div>
              <div className="flex justify-between">
                <div>After Deposit:</div>
                <div>
                  {lamportBalanceWithPrecision + Number(tokensAmountValue)}
                  &nbsp;{selectedMerged?.context?.symbol}
                </div>
              </div>
              <div className="flex justify-between">
                <div>Gas fee:</div>
                <div>{solGasFee} SOL</div>
              </div>
            </div>

            <Button
              variant="primary"
              className={twMerge(
                "w-full",
                amountError || Number(tokensAmountValue) <= 0 ? "bg-none" : ""
              )}
              size="sm"
              isLoading={actionLoading}
              disabled={amountError || Number(tokensAmountValue) <= 0}
              onClick={handleTopUpSolClick}
            >
              Add SOL
            </Button>
            <Button
              variant="primary"
              className="w-full bg-none border-2 border-gray-50 hover:after:border-gray-800"
              size="sm"
              onClick={hideModal}
            >
              Close
            </Button>
          </div>
        </div>
      </div>
    </BaseModal>
  );
};
